import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useJsonExpandPreference } from '@/contexts/UIPreferencesContext';
import {
  ChatBubbleLeftRightIcon,
  ChatBubbleLeftIcon,
  CpuChipIcon,
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CodeBracketIcon,
  ArrowsRightLeftIcon,
  ArrowRightOnRectangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { useCollaborationJournals } from '@/hooks/useCollaborations';
import type { CollaborationJournal } from '@/types';
import { failoverFromText, failoverOf } from '@/utils/journalFailover';

interface CollaborationJournalViewerProps {
  collaborationId: string;
  limit?: number;
  showTitle?: boolean;
  compact?: boolean;
}

// Event type styling
const eventConfig = {
  llm_call: {
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
    borderColor: 'border-blue-200 dark:border-blue-800',
    bgColor: 'bg-blue-50 dark:bg-blue-900/30',
    icon: ChatBubbleLeftIcon,
    label: 'LLM Call',
  },
  tool_call: {
    color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-200',
    borderColor: 'border-violet-200 dark:border-violet-800',
    bgColor: 'bg-violet-50 dark:bg-violet-900/30',
    icon: WrenchScrewdriverIcon,
    label: 'Tool Call',
  },
  completed: {
    color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
    borderColor: 'border-green-200 dark:border-green-800',
    bgColor: 'bg-green-50 dark:bg-green-900/30',
    icon: CheckCircleIcon,
    label: 'Completed',
  },
  failed: {
    color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
    borderColor: 'border-red-200 dark:border-red-800',
    bgColor: 'bg-red-50 dark:bg-red-900/30',
    icon: ExclamationCircleIcon,
    label: 'Failed',
  },
  waiting: {
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/30',
    icon: ClockIcon,
    label: 'Waiting',
  },
};

// Node type icons
const nodeTypeConfig = {
  agent: { icon: CpuChipIcon, label: 'Agent', color: 'text-indigo-500' },
  function: { icon: CodeBracketIcon, label: 'Function', color: 'text-purple-500' },
  branch: { icon: ArrowsRightLeftIcon, label: 'Branch', color: 'text-orange-500' },
  entry: { icon: ArrowRightOnRectangleIcon, label: 'Entry', color: 'text-teal-500' },
};

// Format duration in human-readable format
const formatDuration = (ms: number | null | undefined): string | null => {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

// Highlight matching text within a string
function HighlightText({ text, searchTerm }: { text: string; searchTerm: string }) {
  if (!searchTerm) return <>{text}</>;
  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded px-0.5">{part}</mark>
        ) : (
          part
        )
      )}
    </>
  );
}

// Check if a journal entry matches the search term
function journalMatchesSearch(journal: CollaborationJournal, term: string): boolean {
  if (!term) return true;
  const lower = term.toLowerCase();
  const fields = [
    journal.prompt,
    journal.response,
    journal.error,
    journal.functionName,
    journal.nodeId,
    // A tool call's arguments are the only place its subject appears (the order
    // id, the URL), so search has to reach into them (task 1009).
    journal.event === 'tool_call' && journal.input ? safeStringify(journal.input) : null,
    // So that searching "length" finds every call the output limit cut off,
    // without spending a filter option on it (task 1033).
    journal.event === 'llm_call' && typeof journal.metadata?.finishReason === 'string'
      ? journal.metadata.finishReason
      : null,
  ];
  return fields.some(f => f && f.toLowerCase().includes(lower));
}

// Stringify a JSON value for search/display without throwing on a cycle
function safeStringify(value: unknown, indent?: number): string {
  try {
    return JSON.stringify(value, null, indent) ?? '';
  } catch {
    return '';
  }
}

// One model round trip inside a tool-using LLM call, as stored on the
// `llm_call` row's metadata by the engine (task 1009)
interface JournalStep {
  index: number;
  text: string;
  finishReason: string;
  usage: { inputTokens: number; outputTokens: number };
  toolCallIds: string[];
  durationMs: number;
}

// Read the step timeline off an llm_call row's metadata, tolerating rows
// written before this existed
function extractSteps(metadata: Record<string, unknown> | null): JournalStep[] {
  const steps = metadata?.steps;
  if (!Array.isArray(steps)) return [];
  return steps.filter((s): s is JournalStep => !!s && typeof s === 'object');
}

// Read the offered tool names off an llm_call row's metadata
function extractToolsOffered(metadata: Record<string, unknown> | null): string[] {
  const offered = metadata?.toolsOffered;
  if (!Array.isArray(offered)) return [];
  return offered.filter((name): name is string => typeof name === 'string');
}

// Read the provider warnings off an llm_call row's metadata (task 1033)
function extractWarnings(metadata: Record<string, unknown> | null): string[] {
  const warnings = metadata?.warnings;
  if (!Array.isArray(warnings)) return [];
  return warnings.filter((w): w is string => typeof w === 'string');
}

// The AI SDK's finish reason for a call, when the row carries one (task 1033)
function extractFinishReason(metadata: Record<string, unknown> | null): string | null {
  const reason = metadata?.finishReason;
  return typeof reason === 'string' && reason.length > 0 ? reason : null;
}

// How a finish reason reads: `stop` is the normal ending and gets no badge at
// all; `length` means the answer was cut off by the output limit; a content
// filter or a provider error are failures wearing a success's clothes.
const finishReasonStyles: Record<string, string> = {
  length: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  'content-filter': 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  error: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
};

const NEUTRAL_FINISH_REASON_STYLE =
  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

// Human labels for the token usage keys. The breakdown the AI SDK normalises
// across providers (task 1033) is meaningless as raw camelCase.
const usageLabels: Record<string, string> = {
  inputTokens: 'Input',
  outputTokens: 'Output',
  totalTokens: 'Total',
  cacheReadTokens: 'Cache read',
  cacheWriteTokens: 'Cache write',
  noCacheTokens: 'Uncached input',
  reasoningTokens: 'Reasoning',
  textTokens: 'Text output',
};

// A usage entry worth a chip: zero-valued breakdown lines are noise, since most
// providers report nothing for most of them on any given call.
function isUsageEntryShown(key: string, value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value !== 'number') return true;
  if (value !== 0) return true;
  // The three totals are always meaningful, even at zero.
  return key === 'inputTokens' || key === 'outputTokens' || key === 'totalTokens';
}

export const readTokenCount = (usage: Record<string, unknown> | null, key: string): number => {
  const value = usage?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

/** Whole-number share of input read from the cache, shared by the Journal cache line and the run's Cache tile. */
export const cacheReadPercent = (cacheReadTokens: number, inputTokens: number): number =>
  Math.round((cacheReadTokens / inputTokens) * 100);

/**
 * The cache line on an `llm_call` row (task 1126), drawn only from the usage
 * fields the row already carries. A call that read from the cache gets a bar
 * of its input split into read-from-cache, written-to-cache and uncached; a
 * call on a caching config that read nothing gets a plain-language
 * explanation, because a silent miss is the failure mode; a config with
 * caching off gets nothing.
 */
export function JournalCacheLine({
  usage,
  metadata,
}: {
  usage: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}) {
  if (metadata?.promptCaching === false) return null;

  const inputTokens = readTokenCount(usage, 'inputTokens');
  const cacheReadTokens = readTokenCount(usage, 'cacheReadTokens');
  const cacheWriteTokens = readTokenCount(usage, 'cacheWriteTokens');

  if (cacheReadTokens > 0 && inputTokens > 0) {
    const noCacheTokens =
      typeof usage?.noCacheTokens === 'number'
        ? readTokenCount(usage, 'noCacheTokens')
        : Math.max(inputTokens - cacheReadTokens - cacheWriteTokens, 0);
    const share = (tokens: number) => `${Math.min((tokens / inputTokens) * 100, 100)}%`;
    const readPercent = cacheReadPercent(cacheReadTokens, inputTokens);
    return (
      <div data-testid="journal-cache-line" className="mb-2">
        <div className="flex items-baseline justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span>
            {cacheReadTokens.toLocaleString()} read from cache · {cacheWriteTokens.toLocaleString()} written ·{' '}
            {noCacheTokens.toLocaleString()} uncached
          </span>
          <span className="font-semibold text-blue-700 dark:text-blue-300">{readPercent}% read from cache</span>
        </div>
        <div
          role="img"
          aria-label={`${readPercent}% of input read from cache`}
          className="mt-1 flex h-2 overflow-hidden rounded bg-gray-200 dark:bg-gray-700"
        >
          <span data-testid="cache-bar-read" className="block h-full bg-blue-500" style={{ width: share(cacheReadTokens) }} />
          <span data-testid="cache-bar-write" className="block h-full bg-amber-500" style={{ width: share(cacheWriteTokens) }} />
          <span data-testid="cache-bar-uncached" className="block h-full bg-gray-400 dark:bg-gray-500" style={{ width: share(noCacheTokens) }} />
        </div>
      </div>
    );
  }

  if (cacheReadTokens === 0 && metadata?.promptCaching === true) {
    return (
      <p data-testid="journal-cache-miss" className="mb-2 text-xs text-amber-700 dark:text-amber-300">
        Nothing read from cache: first call in five minutes, or the prompt is under the model's minimum
      </p>
    );
  }

  return null;
}

// Map a step's tool call ids back to the tool names the steps timeline shows
function toolNamesForStep(step: JournalStep, toolCallsById: Map<string, string>): string[] {
  if (!Array.isArray(step.toolCallIds)) return [];
  return step.toolCallIds.map(id => toolCallsById.get(id) || id);
}

// Extract total tokens from usage object
function extractTotalTokens(usage: Record<string, unknown> | null): number {
  if (!usage) return 0;
  // Check common token count keys
  if (typeof usage.totalTokens === 'number') return usage.totalTokens;
  if (typeof usage.total_tokens === 'number') return usage.total_tokens;
  // Try input + output tokens
  const input = typeof usage.inputTokens === 'number' ? usage.inputTokens
    : typeof usage.input_tokens === 'number' ? usage.input_tokens : 0;
  const output = typeof usage.outputTokens === 'number' ? usage.outputTokens
    : typeof usage.output_tokens === 'number' ? usage.output_tokens : 0;
  if (input || output) return input + output;
  return 0;
}

/**
 * Everything recorded on one journal entry, as the expanded entry shows it:
 * error, Model and Model Config, token usage with the cache line, the step
 * timeline, tools offered, the provider response, warnings, prompt, response,
 * and the input and output data. Exported for the Run Explorer (task 1151),
 * which renders its own row header and this body beneath it.
 */
export function JournalEntryBody({
  journal,
  searchTerm = '',
  toolCallsById,
}: {
  journal: CollaborationJournal;
  searchTerm?: string;
  /** Tool name by AI SDK tool call id, so the step timeline can name tools */
  toolCallsById?: Map<string, string>;
}) {
  const { jsonExpandedByDefault } = useJsonExpandPreference();
  const [inputOpen, setInputOpen] = useState(jsonExpandedByDefault);
  const [outputOpen, setOutputOpen] = useState(jsonExpandedByDefault);

  const isReplay = !!journal.originalJournalId;

  // Tool call rows (task 1009): the tool name lives in `functionName`, the
  // arguments in `input` and the result in `output`.
  const isToolCall = journal.event === 'tool_call';
  const isTruncatedResult = journal.metadata?.truncated === true;
  const steps = journal.event === 'llm_call' ? extractSteps(journal.metadata) : [];
  const toolsOffered = journal.event === 'llm_call' ? extractToolsOffered(journal.metadata) : [];

  // The rest of the AI SDK result (task 1033).
  const warnings = journal.event === 'llm_call' ? extractWarnings(journal.metadata) : [];
  const responseModelId =
    journal.event === 'llm_call' && typeof journal.metadata?.responseModelId === 'string'
      ? journal.metadata.responseModelId
      : null;
  const responseId =
    journal.event === 'llm_call' && typeof journal.metadata?.responseId === 'string'
      ? journal.metadata.responseId
      : null;
  const retryAttempts =
    journal.event === 'llm_call' && typeof journal.metadata?.retryAttempts === 'number'
      ? journal.metadata.retryAttempts
      : 0;
  // This call ran on the agent's failover Model Config (task 1300/1301).
  const failover = journal.event === 'llm_call' ? failoverOf(journal.metadata) : null;

  return (
    <div className="px-4 pb-4 space-y-4">
      {/* Replay lineage */}
      {isReplay && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2">
          <ArrowPathIcon className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Replay of journal entry{' '}
            <span className="font-mono font-medium">{journal.originalJournalId!.slice(0, 8)}...</span>
          </span>
          {journal.promptTemplateId && (
            <span className="ml-auto text-amber-600 dark:text-amber-400">
              Template: <span className="font-mono">{journal.promptTemplateId.slice(0, 8)}...</span>
            </span>
          )}
        </div>
      )}

      {/* Error Section (failed events, and tool calls that threw) */}
      {(journal.event === 'failed' || isToolCall) && journal.error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded border border-red-200 dark:border-red-800">
          <h4 className="text-xs font-medium text-red-600 dark:text-red-400 uppercase mb-2 flex items-center">
            <ExclamationCircleIcon className="h-3 w-3 mr-1" />
            Error
          </h4>
          <pre className="text-sm text-red-800 dark:text-red-200 whitespace-pre-wrap font-mono">
            {searchTerm ? <HighlightText text={journal.error} searchTerm={searchTerm} /> : journal.error}
          </pre>
        </div>
      )}

      {/* LLM Config Section */}
      {(journal.llmProvider || journal.llmModel || journal.provider || journal.model) && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
          {(journal.llmProvider || journal.provider) && (
            <div>
              <span className="font-medium">Provider:</span> {journal.llmProvider || journal.provider}
            </div>
          )}
          {(journal.llmModel || journal.model) && (
            <div>
              <span className="font-medium">Model:</span> {journal.llmModel || journal.model}
            </div>
          )}
          {journal.llmTemperature != null && (
            <div>
              <span className="font-medium">Temp:</span> {journal.llmTemperature}
            </div>
          )}
          {journal.llmMaxOutputTokens != null && (
            <div>
              <span className="font-medium">Max Tokens:</span> {journal.llmMaxOutputTokens}
            </div>
          )}
          {journal.modelConfigSource && (
            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
              {journal.modelConfigSource}
            </span>
          )}
        </div>
      )}

      {/* Usage Stats */}
      {journal.usage && Object.keys(journal.usage).length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
            Token Usage
          </h4>
          {journal.event === 'llm_call' && (
            <JournalCacheLine usage={journal.usage} metadata={journal.metadata} />
          )}
          <div className="flex flex-wrap gap-2">
            {Object.entries(journal.usage).flatMap(([key, value]) => {
              if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                return Object.entries(value as Record<string, unknown>)
                  .filter(([subKey, subValue]) => isUsageEntryShown(subKey, subValue))
                  .map(([subKey, subValue]) => (
                    <div
                      key={`${key}.${subKey}`}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs"
                    >
                      <span className="text-gray-500 dark:text-gray-400">
                        {key}.{usageLabels[subKey] ?? subKey}:
                      </span>{' '}
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {String(subValue)}
                      </span>
                    </div>
                  ));
              }
              if (!isUsageEntryShown(key, value)) return [];
              return [(
                <div
                  key={key}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs"
                >
                  <span className="text-gray-500 dark:text-gray-400">
                    {usageLabels[key] ?? key}:
                  </span>{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {typeof value === 'number' ? value.toLocaleString() : String(value)}
                  </span>
                </div>
              )];
            })}
          </div>
        </div>
      )}

      {/* Step timeline (task 1009) — one LLM Call is a loop of model round
          trips, and this is where the time and the tokens went */}
      {steps.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center">
            <ArrowsRightLeftIcon className="h-3 w-3 mr-1" />
            Steps
          </h4>
          <ol className="space-y-1">
            {steps.map(step => {
              const names = toolNamesForStep(step, toolCallsById ?? new Map());
              return (
                <li
                  key={step.index}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 text-xs"
                >
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Step {step.index + 1}
                  </span>
                  {step.durationMs > 0 && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatDuration(step.durationMs)}
                    </span>
                  )}
                  <span className="text-gray-500 dark:text-gray-400">
                    {(step.usage?.inputTokens ?? 0).toLocaleString()} in /{' '}
                    {(step.usage?.outputTokens ?? 0).toLocaleString()} out
                  </span>
                  {names.length > 0 && (
                    <span className="font-mono text-violet-700 dark:text-violet-300">
                      {names.join(', ')}
                    </span>
                  )}
                  {step.finishReason && (
                    <span className="ml-auto px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {step.finishReason}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Tools offered to the agent on this call */}
      {toolsOffered.length > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium">Tools offered:</span>{' '}
          <span className="font-mono">{toolsOffered.join(', ')}</span>
        </div>
      )}

      {/* Provider response (task 1033) — the dated model version actually
          served under the alias, and the request id for a support ticket */}
      {(responseModelId || responseId || retryAttempts > 0 || failover) && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium">Provider response:</span>
          {responseModelId && <span className="font-mono">{responseModelId}</span>}
          {responseId && <span className="font-mono">{responseId}</span>}
          {retryAttempts > 0 && (
            <span className="text-amber-700 dark:text-amber-300">
              {retryAttempts} {retryAttempts === 1 ? 'retry' : 'retries'}
            </span>
          )}
          {failover && (
            <span
              data-testid="journal-failover-mark"
              title={failover.primaryError ?? undefined}
              className="text-amber-700 dark:text-amber-300"
            >
              <span className="font-medium">Ran on failover</span> {failoverFromText(failover)}
            </span>
          )}
        </div>
      )}

      {/* Settings the provider ignored, unsupported tool features */}
      {warnings.length > 0 && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
          <h4 className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase mb-2 flex items-center">
            <ExclamationCircleIcon className="h-3 w-3 mr-1" />
            Provider warnings
          </h4>
          <ul className="space-y-1">
            {warnings.map(warning => (
              <li key={warning} className="text-xs text-amber-800 dark:text-amber-200">
                {warning}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Prompt */}
      {journal.prompt && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center">
            <DocumentTextIcon className="h-3 w-3 mr-1" />
            Prompt
          </h4>
          <div className="p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 max-h-64 overflow-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans">
              {searchTerm ? <HighlightText text={journal.prompt} searchTerm={searchTerm} /> : journal.prompt}
            </pre>
          </div>
        </div>
      )}

      {/* Response */}
      {journal.response && (
        <div>
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2 flex items-center">
            <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
            Response
          </h4>
          <div className="p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 max-h-64 overflow-auto">
            <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-sans">
              {searchTerm ? <HighlightText text={journal.response} searchTerm={searchTerm} /> : journal.response}
            </pre>
          </div>
        </div>
      )}

      {/* Input/Output Data (expandable) */}
      {(journal.input || journal.output) && (
        <div className="space-y-2">
          {journal.input && Object.keys(journal.input).length > 0 && (
            <div>
              <button
                onClick={() => setInputOpen(o => !o)}
                className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {inputOpen ? (
                  <ChevronDownIcon className="h-3 w-3 mr-1" />
                ) : (
                  <ChevronRightIcon className="h-3 w-3 mr-1" />
                )}
                {isToolCall ? 'Arguments' : 'Input Data'}
              </button>
              {inputOpen && (
                <pre className="mt-2 p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs text-gray-800 dark:text-gray-200 overflow-auto max-h-48 font-mono">
                  {safeStringify(journal.input, 2)}
                </pre>
              )}
            </div>
          )}
          {journal.output && Object.keys(journal.output).length > 0 && (
            <div>
              <button
                onClick={() => setOutputOpen(o => !o)}
                className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {outputOpen ? (
                  <ChevronDownIcon className="h-3 w-3 mr-1" />
                ) : (
                  <ChevronRightIcon className="h-3 w-3 mr-1" />
                )}
                {isToolCall ? 'Result' : 'Output Data'}
              </button>
              {isTruncatedResult && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Result truncated at the size limit — only the first part is stored.
                </p>
              )}
              {outputOpen && (
                <pre className="mt-2 p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs text-gray-800 dark:text-gray-200 overflow-auto max-h-48 font-mono">
                  {safeStringify(journal.output, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function JournalEntry({
  journal,
  compact = false,
  forceExpanded,
  searchTerm = '',
  toolCallsById,
}: {
  journal: CollaborationJournal;
  compact?: boolean;
  forceExpanded?: boolean;
  searchTerm?: string;
  /** Tool name by AI SDK tool call id, so the step timeline can name tools */
  toolCallsById?: Map<string, string>;
}) {
  const [localExpanded, setLocalExpanded] = useState(!compact);

  // forceExpanded overrides local state when it changes
  const isExpanded = forceExpanded !== undefined ? forceExpanded : localExpanded;

  const handleToggle = () => {
    setLocalExpanded(!isExpanded);
  };

  // Get event config (fallback to completed if unknown)
  const event = eventConfig[journal.event as keyof typeof eventConfig] || eventConfig.completed;
  const EventIcon = event.icon;

  // Get node type config (fallback to agent if unknown)
  const nodeType = nodeTypeConfig[journal.nodeType as keyof typeof nodeTypeConfig] || nodeTypeConfig.agent;
  const NodeTypeIcon = nodeType.icon;

  const isReplay = !!journal.originalJournalId;

  // Tool call rows (task 1009): the tool name lives in `functionName`, the
  // arguments in `input` and the result in `output`.
  const isToolCall = journal.event === 'tool_call';

  // The finish reason only earns a badge when it is something other than a
  // normal stop (task 1033) — a `length` call is a truncated answer whose fix
  // (raise the output limit) is otherwise invisible.
  const finishReason = journal.event === 'llm_call' ? extractFinishReason(journal.metadata) : null;
  const showFinishReason = !!finishReason && finishReason !== 'stop';

  return (
    <div className={`border rounded-lg overflow-hidden ${event.borderColor} ${event.bgColor}`}>
      {/* Header */}
      <button
        onClick={handleToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/50 dark:hover:bg-black/20 transition-colors"
      >
        <div className="flex items-center space-x-3">
          {compact && (
            isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            )
          )}

          {/* Node Type Icon */}
          <NodeTypeIcon className={`h-5 w-5 ${nodeType.color}`} title={nodeType.label} />

          {/* Agent Name (for agent nodes) */}
          {journal.nodeType === 'agent' && journal.agentId && (
            <div className="flex items-center space-x-1.5">
              <span className="text-xs text-gray-500 dark:text-gray-400">Agent:</span>
              <Link
                to={`/agents/${journal.agentId}`}
                className="text-sm font-semibold text-indigo-600 dark:text-indigo-300 hover:underline truncate max-w-[200px]"
                onClick={(e) => e.stopPropagation()}
              >
                {journal.agent?.name || journal.agentId.slice(0, 8)}
              </Link>
            </div>
          )}

          <div className="flex items-center space-x-2">
            {/* Event Badge */}
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${event.color}`}>
              <EventIcon className="h-3 w-3 inline mr-1" />
              {event.label}
            </span>

            {/* Iteration for LLM calls */}
            {journal.event === 'llm_call' && journal.iteration && (
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Iteration {journal.iteration}
              </span>
            )}

            {/* Finish reason, when the call did not simply stop (task 1033) */}
            {showFinishReason && (
              <span
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  finishReasonStyles[finishReason!] ?? NEUTRAL_FINISH_REASON_STYLE
                }`}
                title="How the model ended this call"
              >
                finish: {finishReason}
              </span>
            )}

            {/* Tool name for tool call rows */}
            {isToolCall && journal.functionName && (
              <span className="text-xs font-mono text-violet-700 dark:text-violet-300">
                {searchTerm ? <HighlightText text={journal.functionName} searchTerm={searchTerm} /> : journal.functionName}
              </span>
            )}

            {/* Error badge for a tool call that failed */}
            {isToolCall && journal.error && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200">
                <ExclamationCircleIcon className="h-3 w-3 mr-0.5" />
                Error
              </span>
            )}

            {/* Function name for function nodes */}
            {journal.nodeType === 'function' && journal.functionName && (
              <span className="text-xs font-mono text-purple-600 dark:text-purple-400">
                {searchTerm ? <HighlightText text={`${journal.functionName}()`} searchTerm={searchTerm} /> : `${journal.functionName}()`}
              </span>
            )}

            {/* Replay indicator */}
            {isReplay && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                <ArrowPathIcon className="h-3 w-3 mr-0.5" />
                Replay
              </span>
            )}

            {/* Prompt template indicator */}
            {journal.promptTemplateId && !isReplay && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                tmpl:{journal.promptTemplateId.slice(0, 6)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* Node ID */}
          {searchTerm && journal.nodeId && (
            <span className="text-xs text-gray-500 dark:text-gray-400 font-mono hidden sm:inline">
              <HighlightText text={journal.nodeId} searchTerm={searchTerm} />
            </span>
          )}

          {/* Duration */}
          {journal.duration != null && (
            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
              <ClockIcon className="h-3 w-3 mr-1" />
              {formatDuration(journal.duration)}
            </span>
          )}

          {/* Model (use new llmModel field, fallback to deprecated model) */}
          {(journal.llmModel || journal.model) && (
            <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
              {journal.llmModel || journal.model}
            </span>
          )}

          {/* Timestamp */}
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {new Date(journal.createdAt).toLocaleTimeString()}
          </span>
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <JournalEntryBody journal={journal} searchTerm={searchTerm} toolCallsById={toolCallsById} />
      )}
    </div>
  );
}

export default function CollaborationJournalViewer({
  collaborationId,
  limit = 20,
  showTitle = true,
  compact = false,
}: CollaborationJournalViewerProps) {
  const [page, setPage] = useState(1);
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [nodeTypeFilter, setNodeTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [globalExpanded, setGlobalExpanded] = useState<boolean | undefined>(undefined);

  // Build server-side filters from dropdown values
  const journalFilters = {
    event: eventFilter !== 'all' ? eventFilter : undefined,
    nodeType: nodeTypeFilter !== 'all' ? nodeTypeFilter : undefined,
  };

  const { data: journalsData, isLoading, error } = useCollaborationJournals(
    collaborationId,
    page,
    limit,
    journalFilters
  );

  const journals = journalsData?.data || [];
  const pagination = journalsData?.pagination;

  // Client-side search filtering on fetched results
  const filteredJournals = useMemo(() => {
    if (!searchTerm) return journals;
    return journals.filter((j: CollaborationJournal) => journalMatchesSearch(j, searchTerm));
  }, [journals, searchTerm]);

  // Compute summary stats from the current page of results (pre-search-filter, so stats reflect server data)
  const stats = useMemo(() => {
    const totalDuration = journals.reduce((sum: number, j: CollaborationJournal) => sum + (j.duration || 0), 0);
    const llmCallCount = journals.filter((j: CollaborationJournal) => j.event === 'llm_call').length;
    const toolCallCount = journals.filter((j: CollaborationJournal) => j.event === 'tool_call').length;
    const totalTokens = journals.reduce((sum: number, j: CollaborationJournal) => sum + extractTotalTokens(j.usage), 0);
    const replayCount = journals.filter((j: CollaborationJournal) => !!j.originalJournalId).length;
    return { totalDuration, llmCallCount, toolCallCount, totalTokens, replayCount };
  }, [journals]);

  // Tool name by tool call id, so an LLM Call's step timeline can name the
  // tools each round trip asked for (task 1009).
  const toolCallsById = useMemo(() => {
    const map = new Map<string, string>();
    journals.forEach((j: CollaborationJournal) => {
      const toolCallId = j.metadata?.toolCallId;
      if (j.event === 'tool_call' && typeof toolCallId === 'string' && j.functionName) {
        map.set(toolCallId, j.functionName);
      }
    });
    return map;
  }, [journals]);

  const handleExpandAll = useCallback(() => {
    setGlobalExpanded(true);
  }, []);

  const handleCollapseAll = useCallback(() => {
    setGlobalExpanded(false);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {showTitle && (
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Collaboration Journal
          </h3>
        )}
        <div className="text-gray-500 dark:text-gray-400">Loading journal entries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        {showTitle && (
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Collaboration Journal
          </h3>
        )}
        <div className="text-red-500 dark:text-red-400">Error loading journal entries</div>
      </div>
    );
  }

  const hasActiveFilters = eventFilter !== 'all' || nodeTypeFilter !== 'all';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      {showTitle && (
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-400" />
          Collaboration Journal
          {pagination && (
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({pagination.total} entries)
            </span>
          )}
        </h3>
      )}

      {/* Summary stats bar */}
      {journals.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4">
          {pagination && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              {pagination.total} entries
            </span>
          )}
          {stats.llmCallCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200">
              <ChatBubbleLeftIcon className="h-3 w-3 mr-1" />
              {stats.llmCallCount} LLM calls
            </span>
          )}
          {stats.toolCallCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-200">
              <WrenchScrewdriverIcon className="h-3 w-3 mr-1" />
              {stats.toolCallCount} tool calls
            </span>
          )}
          {stats.totalDuration > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200">
              <ClockIcon className="h-3 w-3 mr-1" />
              {formatDuration(stats.totalDuration)}
            </span>
          )}
          {stats.totalTokens > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200">
              {stats.totalTokens.toLocaleString()} tokens
            </span>
          )}
          {stats.replayCount > 0 && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200">
              <ArrowPathIcon className="h-3 w-3 mr-1" />
              {stats.replayCount} replays
            </span>
          )}
        </div>
      )}

      {/* Filters + Search + Expand/Collapse */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <FunnelIcon className="h-4 w-4 text-gray-400" />

        <select
          value={eventFilter}
          onChange={(e) => { setEventFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="all">All Events</option>
          <option value="llm_call">LLM Calls</option>
          <option value="tool_call">Tool Calls</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="waiting">Waiting</option>
        </select>

        <select
          value={nodeTypeFilter}
          onChange={(e) => { setNodeTypeFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="all">All Node Types</option>
          <option value="agent">Agent</option>
          <option value="function">Function</option>
          <option value="branch">Branch</option>
          <option value="entry">Entry</option>
        </select>

        {/* Search input */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search entries..."
            className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded pl-7 pr-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Expand/Collapse All */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={handleExpandAll}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Expand All
          </button>
          <button
            onClick={handleCollapseAll}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Collapse All
          </button>
        </div>

        {(hasActiveFilters || searchTerm) && (
          <button
            onClick={() => {
              setEventFilter('all');
              setNodeTypeFilter('all');
              setSearchTerm('');
              setPage(1);
            }}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {filteredJournals.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {hasActiveFilters || searchTerm
            ? 'No entries match the selected filters.'
            : 'No journal entries recorded for this collaboration.'}
        </p>
      ) : (
        <div className="space-y-3">
          {searchTerm && filteredJournals.length !== journals.length && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing {filteredJournals.length} of {journals.length} entries matching &quot;{searchTerm}&quot;
            </p>
          )}
          {filteredJournals.map((journal: CollaborationJournal) => (
            <JournalEntry
              key={journal.id}
              journal={journal}
              compact={compact}
              forceExpanded={globalExpanded}
              searchTerm={searchTerm}
              toolCallsById={toolCallsById}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={!pagination.hasPrev}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!pagination.hasNext}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
